import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { AppError } from "../../shared/errors/app-error.js";
import { router } from "../router/router.js";
import { CLIENT_WITHOUT_PLH } from "../../application/services/project-responsibility-service.js";
import { openClientPlhSetup } from "../navigation/client-plh-deep-link.js";
import {
  TECHNICAL_DOCUMENT_TYPE_LABELS,
  TECHNICAL_REVIEW_STATUS_LABELS,
  reviewLocksDocument,
  submitActionFor,
  type TechnicalDocumentReview,
  type TechnicalDocumentType
} from "../../domain/review/technical-document-review.js";

/**
 * Peças de interface do mecanismo central "ENVIAR PARA O PLH", compartilhadas
 * pelas páginas de elaboração (LP, ME, PM…): botão, banner de status /
 * correção e o modal de envio. A regra fica no TechnicalReviewService; aqui
 * só se renderiza o estado que ele devolve.
 */

function esc(value: unknown): string {
  return String(value ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] || c));
}

function ctx(clientId?: string | null): any {
  const c = contextStore.get();
  return { activeClientId: clientId || c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
}

/** O botão da barra de ações: "Enviar para o PLH" / "Reenviar para o PLH", só quando cabe. */
export function plhSubmitButton(review: TechnicalDocumentReview | null, documentStatus: string, buttonClass = "btn"): string {
  const action = submitActionFor(review, documentStatus, contextStore.hasPermission(PermissionCode.TechnicalReviewSubmitToPlh));
  if (!action.show) return "";
  return `<button class="${buttonClass} plh-workflow" id="btn-submit-to-plh"><i data-lucide="send"></i> ${esc(action.label)}</button>`;
}

/**
 * APROVAR RELATÓRIO — quem pode, e quando.
 *
 * As três condições do comando, na ordem: o documento está EM APROVAÇÃO DO
 * PLH; quem está logado é o PLH **atribuído** a este documento (que o serviço
 * resolve a partir do PLH responsável do Cliente); e esse usuário tem a
 * permissão de aprovação. Faltando qualquer uma, o botão não existe.
 *
 * Isto decide só o que se DESENHA. A decisão que vale é a do servidor, em
 * `TechnicalReviewService.approve()`, que reconfere permissão, elegibilidade
 * de PLH para o Cliente, atribuição e status — esconder o botão nunca foi
 * segurança.
 */
export function plhReviewActions(review: TechnicalDocumentReview | null, options: { hold?: boolean } = {}): { canApprove: boolean; canReturn: boolean; canStart: boolean } {
  if (!review || options.hold) return { canApprove: false, canReturn: false, canStart: false };
  const underReview = review.status === "AWAITING_PLH_REVIEW" || review.status === "PLH_REVIEWING";
  const mine = contextStore.get().professionalId || null;
  const assigned = !!mine && String(review.assignedPlhProfessionalId) === String(mine);
  return {
    // INICIAR ANÁLISE só faz sentido enquanto o documento AGUARDA. Depois de
    // iniciada, a ação some e dão lugar a corrigir/aprovar.
    canStart: assigned && review.status === "AWAITING_PLH_REVIEW" && contextStore.hasPermission(PermissionCode.TechnicalReviewReview),
    canApprove: assigned && underReview && contextStore.hasPermission(PermissionCode.TechnicalReviewApprove),
    canReturn: assigned && underReview && contextStore.hasPermission(PermissionCode.TechnicalReviewRequestCorrection)
  };
}

/**
 * POR QUE ESTE USUÁRIO NÃO PODE AGIR — em uma frase, para a tela mostrar.
 *
 * Existe porque os botões apenas DESAPARECIAM quando alguma condição falhava.
 * Quem abria o relatório via um card vazio e concluía que o sistema estava
 * quebrado; não havia como distinguir "não sou o PLH deste Cliente" de "meu
 * Profissional não tem assinatura" ou "meu perfil não tem a permissão".
 * Esconder é o comportamento certo para o botão, e o errado para a
 * explicação.
 *
 * `null` quando a pessoa PODE agir.
 */
export function plhActionBlockReason(review: TechnicalDocumentReview | null, options: { hold?: boolean } = {}): string | null {
  if (!review) return "Este documento ainda não foi liberado para análise do PLH.";
  if (options.hold) return "O documento está EM HOLD. Libere o HOLD para retomar a análise.";

  const ctx = contextStore.get();
  const mine = ctx.professionalId || null;
  if (!mine) {
    return "Sua conta não possui um Profissional vinculado. Peça à Administração para vincular em Pessoas e Acessos.";
  }
  if (String(review.assignedPlhProfessionalId) !== String(mine)) {
    return `A análise deste documento está atribuída a ${review.assignedPlhName || "outro Engenheiro PLH"}. Somente o PLH responsável pelo Cliente do documento pode analisá-lo.`;
  }
  if (review.status === "CORRECTION_REQUESTED") {
    return "Correção solicitada: o documento está com o elaborador. Ele voltará para análise quando for reenviado.";
  }
  if (review.status === "PLH_APPROVED") {
    return "Este documento já foi aprovado. Para alterá-lo, crie uma nova revisão.";
  }

  const missing: string[] = [];
  if (!contextStore.hasPermission(PermissionCode.TechnicalReviewReview)) missing.push("analisar");
  if (!contextStore.hasPermission(PermissionCode.TechnicalReviewApprove)) missing.push("aprovar");
  if (!contextStore.hasPermission(PermissionCode.TechnicalReviewRequestCorrection)) missing.push("solicitar correção");
  if (missing.length === 3) {
    // UX Correção: mensagem redundante removida; botões já são ocultados via plhReviewActions().can*
    return null;
  }
  if (missing.length > 0) {
    // UX Correção: mensagem redundante removida; botões específicos já são ocultados via plhReviewActions().can*
    return null;
  }
  return null;
}

/** Botão INICIAR ANÁLISE — o primeiro passo do workflow, que faltava na tela. */
export function plhStartAnalysisButton(review: TechnicalDocumentReview | null, buttonClass = "btn", options: { hold?: boolean } = {}): string {
  if (!plhReviewActions(review, options).canStart) return "";
  return `<button class="${buttonClass} plh-start" id="btn-plh-start"><i data-lucide="play"></i> INICIAR ANÁLISE</button>`;
}

/**
 * CARD "ANÁLISE DO PLH" — o painel global do workflow de aprovação.
 *
 * Mesmo componente para Integridade, END e AVR: recebe o registro de revisão
 * e desenha estado, PLH responsável, início da análise e as ações possíveis.
 * Quando não há ação possível, desenha o MOTIVO — nunca um card vazio.
 */
export function plhApprovalPanel(
  review: TechnicalDocumentReview | null,
  options: { hold?: boolean; buttonClass?: string } = {}
): string {
  const buttonClass = options.buttonClass || "btn-action";
  const actions = plhReviewActions(review, options);
  const reason = plhActionBlockReason(review, options);
  const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

  const statusLabel = review
    ? (review.status === "PLH_APPROVED" && review.finalizedAt ? "Finalizado" : TECHNICAL_REVIEW_STATUS_LABELS[review.status])
    : "Não enviado para análise";

  const buttons = [
    plhStartAnalysisButton(review, buttonClass, options),
    plhReturnButton(review, buttonClass, options),
    plhApproveButton(review, `${buttonClass} primary`, options)
  ].filter(Boolean).join("");

  return `
    <section class="plh-panel">
      <div class="plh-panel-head">ANÁLISE DO PLH</div>
      <div class="plh-panel-grid">
        <span><b>Status:</b> ${esc(statusLabel)}</span>
        <span><b>PLH responsável:</b> ${esc(review?.assignedPlhName || "não definido")}</span>
        <span><b>Início da análise:</b> ${esc(when(review?.reviewStartedAt))}</span>
        ${review?.approvedAt ? `<span><b>Aprovado em:</b> ${esc(when(review.approvedAt))}</span>` : ""}
      </div>
      ${buttons ? `<div class="plh-panel-actions">${buttons}</div>` : ""}
      ${!buttons && reason ? `<p class="plh-panel-reason">${esc(reason)}</p>` : ""}
    </section>`;
}

/** Botão APROVAR RELATÓRIO — só para o PLH atribuído, só em análise. */
export function plhApproveButton(review: TechnicalDocumentReview | null, buttonClass = "btn", options: { hold?: boolean } = {}): string {
  if (!plhReviewActions(review, options).canApprove) return "";
  return `<button class="${buttonClass} plh-approve" id="btn-plh-approve"><i data-lucide="clipboard-check"></i> APROVAR RELATÓRIO</button>`;
}

/**
 * Botão DEVOLVER PARA CORREÇÃO.
 *
 * Não abre um campo de "motivo" solto: a devolução do PLH é POR ITEM (seção +
 * item + observação), regra já estabelecida no Painel de Engenharia e exigida
 * pelo servidor (`validateCorrectionItems` recusa um motivo genérico sem
 * itens). Por isso este botão leva à tela de análise, onde esse formulário
 * existe, em vez de duplicar aqui um caminho mais fraco que o servidor
 * rejeitaria.
 */
export function plhReturnButton(review: TechnicalDocumentReview | null, buttonClass = "btn", options: { hold?: boolean } = {}): string {
  if (!plhReviewActions(review, options).canReturn) return "";
  return `<button class="${buttonClass} plh-return" id="btn-plh-return"><i data-lucide="message-square-warning"></i> DEVOLVER PARA CORREÇÃO</button>`;
}

/**
 * LIGA os botões do card ANÁLISE DO PLH ao serviço central.
 *
 * Uma função só para todas as telas. Antes, apenas o ME tinha esses botões, e
 * LP, PM, US-Convencional e AVR não ofereciam INICIAR ANÁLISE, ENVIAR PARA
 * CORREÇÃO nem APROVAR — o PLH abria o relatório e não tinha o que clicar.
 * Duplicar a fiação em cada página faria as quatro divergirem com o tempo.
 *
 * `onChanged` recebe a revisão atualizada; a página decide o que recarregar.
 * `onFailure` recebe a mensagem do servidor — a recusa é MOSTRADA, nunca
 * engolida: foi a ausência de explicação que fez o sistema parecer quebrado.
 */
export function bindPlhApprovalPanel(
  host: ParentNode,
  options: {
    review: TechnicalDocumentReview | null;
    clientId: string;
    context: (clientId: string) => unknown;
    onChanged: (review: TechnicalDocumentReview) => void | Promise<void>;
    onFailure: (message: string) => void;
    /** Devolução por item exige o formulário da tela de análise. */
    onReturnRequested?: () => void;
  }
): void {
  const review = options.review;
  if (!review) return;
  const service = () => (window as unknown as { app: { platform: { technicalReviewService: any } } }).app.platform.technicalReviewService;
  const ctx = () => options.context(options.clientId);
  const fail = (e: unknown) =>
    options.onFailure(e instanceof AppError ? ((e as { safeMessage?: string }).safeMessage || (e as Error).message) : "Não foi possível concluir a ação.");

  host.querySelector("#btn-plh-start")?.addEventListener("click", async () => {
    try { await options.onChanged(await service().startReview(ctx(), review.id)); }
    catch (e) { fail(e); }
  });

  host.querySelector("#btn-plh-approve")?.addEventListener("click", async () => {
    if (!window.confirm(
      `APROVAR ${review.summary?.documentNumber || "este relatório"} (Rev. ${review.revision})?\n\n` +
      "Ao confirmar, a revisão é congelada e o documento não poderá mais ser editado. " +
      "Para alterá-lo depois, será preciso criar uma nova revisão."
    )) return;
    try { await options.onChanged(await service().approve(ctx(), review.id, {})); }
    catch (e) { fail(e); }
  });

  host.querySelector("#btn-plh-return")?.addEventListener("click", () => {
    // A devolução do PLH é POR ITEM (seção + item + observação) e o servidor
    // recusa motivo genérico. Por isso leva ao formulário que existe, em vez
    // de abrir aqui um caminho mais fraco que seria rejeitado.
    if (options.onReturnRequested) options.onReturnRequested();
    else router.navigate(`/engineering/review/${review.id}`);
  });
}

/** Confirmação do item 4 — diz exatamente o que a aprovação faz, antes de fazer. */
export function plhApproveConfirmModal(open: boolean, busy: boolean, failure?: string | null): string {
  if (!open) return "";
  return `
    <div class="plh-modal-backdrop">
      <div class="plh-modal" style="max-width:560px;">
        <div class="plh-modal-head"><span>APROVAR RELATÓRIO?</span></div>
        <div class="plh-modal-body">
          <p>Ao confirmar, este Relatório de Medição de Espessura será aprovado pelo PLH e finalizado.</p>
          <p style="margin:0;">Após a aprovação:</p>
          <ul style="margin:0; padding-left:20px; line-height:1.7;">
            <li>a revisão será congelada;</li>
            <li>o documento não poderá mais ser editado;</li>
            <li>os signatários serão congelados;</li>
            <li>o PDF final será consolidado;</li>
            <li>o status passará para FINALIZADO.</li>
          </ul>
          ${failure ? `<div class="plh-errors">${esc(failure)}</div>` : ""}
          <div class="plh-actions">
            <button class="plh-btn" id="btn-plh-approve-cancel" ${busy ? "disabled" : ""}>CANCELAR</button>
            <button class="plh-btn primary" id="btn-plh-approve-confirm" ${busy ? "disabled" : ""}>${busy ? "APROVANDO..." : "CONFIRMAR APROVAÇÃO"}</button>
          </div>
        </div>
      </div>
    </div>`;
}

/** Editar fica bloqueado enquanto o documento está aguardando / em análise / aprovado. */
export function reviewEditingLocked(review: TechnicalDocumentReview | null): boolean {
  return !!review && reviewLocksDocument(review.status);
}

/** Banner de situação da revisão e da correção solicitada. */
export function reviewBanner(review: TechnicalDocumentReview | null): string {
  if (!review) return "";
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");
  if (review.status === "CORRECTION_REQUESTED") {
    return `
      <div class="plh-banner plh-banner-correction">
        <strong>CORREÇÃO SOLICITADA PELO PLH</strong>
        <div class="plh-banner-grid">
          <span><b>PLH:</b> ${esc(review.assignedPlhName)}</span>
          <span><b>Data:</b> ${esc(when(review.correctionRequestedAt))}</span>
          <span><b>Motivo:</b> ${esc(review.correctionReason)}</span>
          <span><b>Seção:</b> ${esc(review.correctionSection || "—")}</span>
        </div>
        <small>Corrija o documento e use "Reenviar para o PLH". O mesmo documento e o mesmo ciclo de revisão são preservados.</small>
      </div>`;
  }
  if (review.finalizedAt) {
    return `
      <div class="plh-banner plh-banner-approved">
        <strong>${review.signedAt ? "FINALIZADO" : "FINALIZADO — AGUARDANDO ASSINATURA"}</strong> — finalizado em ${esc(when(review.finalizedAt))}${review.finalizedBy ? ` por ${esc(review.finalizedBy.name)}` : ""} (Rev. ${esc(review.revision)}). A edição ordinária está bloqueada; ${review.signedAt ? "documento assinado." : "a assinatura é o passo seguinte no módulo do documento."}
      </div>`;
  }
  const label = TECHNICAL_REVIEW_STATUS_LABELS[review.status];
  const approved = review.status === "PLH_APPROVED";
  return `
    <div class="plh-banner ${approved ? "plh-banner-approved" : "plh-banner-review"}">
      <strong>${esc(label.toUpperCase())}</strong> — PLH: ${esc(review.assignedPlhName)} · enviado em ${esc(when(review.submittedAt))} por ${esc(review.submittedBy.name)} (ciclo ${review.cycle}).
      ${approved ? ` Aprovado em ${esc(when(review.approvedAt))}${review.reviewComment ? ` · Parecer: ${esc(review.reviewComment)}` : ""}. A assinatura é o passo seguinte.` : " A edição técnica está bloqueada: alterações só pelo fluxo de correção. Visualizar, PDF e Histórico continuam disponíveis."}
    </div>`;
}

export const PLH_REVIEW_UI_STYLES = `
  .plh-workflow { background:#7c3aed !important; border-color:#7c3aed !important; color:#fff !important; }
  .plh-approve { background:#059669 !important; border-color:#059669 !important; color:#fff !important; }
  .plh-return { background:#b45309 !important; border-color:#b45309 !important; color:#fff !important; }
  .plh-start { background:#7c3aed !important; border-color:#7c3aed !important; color:#fff !important; }
  /* Card ANÁLISE DO PLH — o painel global do workflow de aprovação. */
  .plh-panel { border:1px solid rgba(124,58,237,0.45); background: rgba(124,58,237,0.08); border-radius:8px; padding:12px 14px; margin-bottom:14px; }
  .plh-panel-head { font-size:0.72rem; font-weight:700; letter-spacing:0.05em; color: var(--ia-fg, #ddd6fe); margin-bottom:8px; }
  .plh-panel-grid { display:flex; flex-wrap:wrap; gap:6px 22px; font-size:0.8rem; color: var(--color-text-primary, #EDEDED); }
  .plh-panel-actions { display:flex; flex-wrap:wrap; gap:10px; margin-top:12px; }
  .plh-panel-reason { margin:10px 0 0 0; font-size:0.78rem; color: var(--ia-warning-fg, #fde68a); line-height:1.5; }
  .plh-banner { padding:10px 14px; border-radius:6px; font-size:0.82rem; margin-bottom:12px; border:1px solid; display:flex; flex-direction:column; gap:6px; }
  .plh-banner-review { background: rgba(124,58,237,0.12); border-color: rgba(124,58,237,0.5); color: var(--ia-fg, #ddd6fe); }
  .plh-banner-approved { background: rgba(16,185,129,0.12); border-color: rgba(16,185,129,0.5); color: var(--ia-success-fg, #a7f3d0); }
  .plh-banner-correction { background: rgba(245,158,11,0.12); border-color: rgba(245,158,11,0.5); color: var(--ia-warning-fg, #fde68a); }
  .plh-banner-grid { display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap:4px 16px; }
  .plh-banner b { font-weight:700; }
  .plh-banner small { opacity:0.85; }
  .plh-modal-backdrop { position:fixed; inset:0; background:rgba(0,0,0,0.82); display:flex; align-items:center; justify-content:center; z-index:10001; padding:20px; }
  .plh-modal { background: var(--ia-bg-panel, #161A22); border: 1px solid var(--ia-line, #262C38); border-radius:12px; width:100%; max-width:820px; max-height:92vh; display:flex; flex-direction:column; overflow:hidden; color: var(--ia-fg, #E6E9EF); font-size:0.84rem; }
  .plh-modal-head { padding:12px 18px; border-bottom:1px solid #262C38; display:flex; justify-content:space-between; align-items:center; font-weight:700; }
  .plh-modal-body { padding:16px 18px; overflow:auto; display:flex; flex-direction:column; gap:12px; }
  .plh-grid { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:10px; }
  .plh-field { display:flex; flex-direction:column; gap:4px; font-size:0.7rem; color: var(--ia-fg-soft, #A7B0C0); min-width:0; }
  .plh-field .plh-ctl { background: var(--ia-bg-panel, #0F131A); border: 1px solid var(--ia-line, #2B3340); border-radius:6px; padding:8px 10px; color: var(--ia-fg, #E6E9EF); font-size:0.82rem; width:100%; box-sizing:border-box; }
  .plh-field .plh-ctl[readonly] { background: var(--ia-bg-panel, #121620); color: var(--ia-fg-soft, #B9C2D0); }
  .plh-note { padding:10px 14px; border-radius:6px; background: rgba(96,165,250,0.1); border:1px solid rgba(96,165,250,0.35); color: var(--ia-info-fg, #93c5fd); }
  .plh-errors { padding:10px 14px; border-radius:6px; background: rgba(239,68,68,0.1); border:1px solid rgba(239,68,68,0.35); color: var(--ia-danger-fg, #fca5a5); }
  .plh-errors ul { margin:6px 0 0 0; padding-left:18px; }
  .plh-actions { display:flex; justify-content:flex-end; gap:8px; }
  .plh-btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; border: 1px solid var(--ia-line, #2B3340); background: var(--ia-bg-panel, #1B212C); color: var(--ia-fg, #E6E9EF); font-size:0.84rem; font-weight:600; cursor:pointer; }
  .plh-btn[disabled] { opacity:0.5; cursor:not-allowed; }
  .plh-btn.primary { background:#7c3aed; border-color:#7c3aed; color:#fff; }
  .plh-icon-btn { background:transparent; border:none; color: var(--ia-fg-muted, #8A94A6); cursor:pointer; font-size:1rem; }
  @media (max-width: 800px) { .plh-grid, .plh-banner-grid { grid-template-columns: 1fr; } }
`;

export interface PlhSubmitDocumentInfo {
  documentType: TechnicalDocumentType;
  documentId: string;
  clientId: string;
  documentNumber: string;
  revision: string;
  client: string;
  project: string;
  unitArea: string;
  tag: string;
  osNumber: string;
  subOsNumber: string;
  elaboratedBy: string;
}

/**
 * <app-plh-submit-modal> — "ENVIAR PARA REVISÃO E APROVAÇÃO DO PLH".
 * Valida o documento pelo próprio módulo ao abrir; lista só PLHs habilitados
 * pelo gate; nada pré-selecionado; envia pelo serviço central.
 */
export class PlhSubmitModal extends HTMLElement {
  info: PlhSubmitDocumentInfo | null = null;
  onSubmitted: ((review: TechnicalDocumentReview) => void) | null = null;
  onClosed: (() => void) | null = null;

  private resolved: { plh: { professionalId: string; name: string; functionTitle: string; credential: string } | null; engineer: { professionalId: string; name: string } | null; project: { id: string; code: string; name: string } | null; reason: string } | null = null;
  private note = "";
  private errors: string[] = [];
  private validationDone = false;
  private sending = false;
  private failure: string | null = null;

  private get svc(): any { return (window.app.platform as any).technicalReviewService; }

  connectedCallback() {
    this.render();
    this.prepare();
  }

  private async prepare() {
    if (!this.info) return;
    const c = ctx(this.info.clientId);
    try {
      const [validation, resolved] = await Promise.all([
        this.svc.validateForSubmission(c, this.info.documentType, this.info.documentId),
        this.svc.resolvePlhFor(c, this.info.documentType, this.info.documentId)
      ]);
      this.errors = validation.valid ? [] : validation.errors;
      this.resolved = resolved;
    } catch (e: any) {
      this.failure = e instanceof AppError ? ((e as any).safeMessage || e.message) : (e?.message || "Falha ao preparar o envio.");
    }
    this.validationDone = true;
    this.render();
  }

  private async submit() {
    if (!this.info || this.sending) return;
    if (!this.resolved?.plh) { this.failure = this.resolved?.reason || CLIENT_WITHOUT_PLH; this.render(); return; }
    this.sending = true;
    this.failure = null;
    this.render();
    try {
      const review = await this.svc.submit(ctx(this.info.clientId), this.info.documentType, this.info.documentId, { note: this.note });
      this.onSubmitted?.(review);
      this.remove();
    } catch (e: any) {
      this.failure = e instanceof AppError ? ((e as any).safeMessage || e.message) : (e?.message || "Erro ao enviar para o PLH.");
      this.sending = false;
      this.render();
    }
  }

  private close() {
    this.onClosed?.();
    this.remove();
  }

  render() {
    const i = this.info;
    if (!i) { this.innerHTML = ""; return; }
    const field = (label: string, value: string) => `<label class="plh-field"><span>${esc(label)}</span><input class="plh-ctl" readonly value="${esc(value || "—")}"></label>`;
    const canSend = this.validationDone && this.errors.length === 0 && !this.failure?.startsWith("Falha") && !!this.resolved?.plh && !this.sending;
    const r = this.resolved;
    this.innerHTML = `
      <style>${PLH_REVIEW_UI_STYLES}</style>
      <div class="plh-modal-backdrop">
        <div class="plh-modal">
          <div class="plh-modal-head"><span>ENVIAR PARA REVISÃO E APROVAÇÃO DO PLH</span><button class="plh-icon-btn" id="plh-close">✕</button></div>
          <div class="plh-modal-body">
            <div class="plh-grid">
              ${field("Tipo de documento", TECHNICAL_DOCUMENT_TYPE_LABELS[i.documentType])}${field("Nº do relatório", i.documentNumber)}${field("Revisão", i.revision)}
              ${field("Cliente", i.client)}${field("Projeto", i.project)}${field("Unidade / Área", i.unitArea)}
              ${field("Equipamento / TAG", i.tag)}${field("OS", i.osNumber)}${field("Sub-OS", i.subOsNumber)}
              ${field("Responsável pela elaboração", i.elaboratedBy)}
            </div>
            ${!this.validationDone ? `<div class="plh-note">Validando o documento…</div>` : this.errors.length > 0 ? `<div class="plh-errors"><strong>O documento não pode ser enviado com pendências:</strong><ul>${this.errors.map(e => `<li>${esc(e)}</li>`).join("")}</ul></div>` : `<div class="plh-note">Documento validado. Após o envio, esta revisão ficará em análise do PLH e a edição técnica será bloqueada até a aprovação ou a devolução para correção.</div>`}
            <div class="plh-grid" style="grid-template-columns: 1fr 1fr 1fr;">
              ${field("Projeto (fonte dos responsáveis)", r?.project ? `${r.project.code} — ${r.project.name}` : "")}
              ${field("Engenheiro responsável (Projeto)", r?.engineer?.name || "")}
              <label class="plh-field"><span>PLH responsável (Cliente)</span><input class="plh-ctl" readonly value="${esc(r?.plh ? `${r.plh.name}${r.plh.functionTitle ? " — " + r.plh.functionTitle : ""}${r.plh.credential ? " · " + r.plh.credential : ""}` : "—")}">
                <small style="color: var(--ia-fg-muted, #8A94A6);">Definido no cadastro do Cliente; passa pelo gate oficial de PLH. Não há seleção manual.</small></label>
            </div>
            ${this.validationDone && r && !r.plh ? `<div class="plh-errors">${esc(r.reason || CLIENT_WITHOUT_PLH)}<div style="margin-top:8px;"><button class="plh-btn" id="plh-configure-client">CONFIGURAR PLH DO CLIENTE</button></div></div>` : ""}
            <label class="plh-field"><span>Observação para o PLH</span><textarea class="plh-ctl" id="plh-note" rows="3" placeholder="Opcional">${esc(this.note)}</textarea></label>
            ${this.failure ? `<div class="plh-errors">${esc(this.failure)}</div>` : ""}
            <div class="plh-actions">
              <button class="plh-btn" id="plh-cancel">Cancelar</button>
              <button class="plh-btn primary" id="plh-send" ${canSend ? "" : "disabled"}>${this.sending ? "Enviando..." : "Enviar para o PLH"}</button>
            </div>
          </div>
        </div>
      </div>`;
    this.querySelector("#plh-close")?.addEventListener("click", () => this.close());
    this.querySelector("#plh-cancel")?.addEventListener("click", () => this.close());
    this.querySelector("#plh-note")?.addEventListener("input", e => { this.note = (e.target as HTMLTextAreaElement).value; });
    this.querySelector("#plh-send")?.addEventListener("click", () => this.submit());
    this.querySelector("#plh-configure-client")?.addEventListener("click", () => {
      const clientId = this.info?.clientId;
      if (!clientId) return;
      this.close();
      openClientPlhSetup(clientId);
    });
  }
}

if (!customElements.get("app-plh-submit-modal")) {
  customElements.define("app-plh-submit-modal", PlhSubmitModal);
}

/** Abre o modal sobre a página (append no body) e devolve o elemento. */
export function openPlhSubmitModal(info: PlhSubmitDocumentInfo, onSubmitted: (review: TechnicalDocumentReview) => void, onClosed?: () => void): PlhSubmitModal {
  const modal = document.createElement("app-plh-submit-modal") as PlhSubmitModal;
  modal.info = info;
  modal.onSubmitted = onSubmitted;
  modal.onClosed = onClosed ?? null;
  document.body.appendChild(modal);
  return modal;
}
